import React, {useCallback, useEffect, useState} from 'react';
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {InputSwitch} from "primereact/inputswitch";
import {ProgressSpinner} from "primereact/progressspinner";
import {Dropdown} from "primereact/dropdown";
import {Message} from "primereact/message";
import {useDispatch} from "react-redux";
import {useTranslate} from "../../../system/useTranslate";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import Span from "../../../system/components/span";
import {useTaskDelay} from "../../../models/task-delay/use-task-delay";
import {useTaskDelayReason} from "../../../models/task-delay-reason/use-task-delay-reason";
import SelectZofinoDateTime from "./select-zofino-datetime";
import SelectMember from "./select-member";
import {formatDate} from "../../../system/utils";
import {setMessage} from "../../../system/app-slice";
import InputLabel from "../../../system/components/input-label";

const REASON_MAX_LENGTH = 80;
const NOTE_MAX_LENGTH = 1000;

function normalizeTitle(value) {
    return String(value || "").trim().toLowerCase();
}

function localizedTaskDelayError(error, translate, fallbackKey) {
    const code = error?.code || error?.details?.code || error?.message;
    if (typeof code === "string" && code.startsWith("task-delay.error.")) {
        return translate(code);
    }
    return translate(fallbackKey);
}

function TaskDelayModal({visible, setVisible, task, updateTask, members = []}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const taskDelay = useTaskDelay();
    const taskDelayReason = useTaskDelayReason();

    const [reasons, setReasons] = useState([]);
    const [delays, setDelays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const [selectedReasonId, setSelectedReasonId] = useState(null);
    const [newDueAt, setNewDueAt] = useState(null);
    const [quickChoice, setQuickChoice] = useState(null);
    const [newAssignee, setNewAssignee] = useState(null);
    const [note, setNote] = useState("");
    const [notifyRelatedUsers, setNotifyRelatedUsers] = useState(true);

    const [addingReason, setAddingReason] = useState(false);
    const [newReasonTitle, setNewReasonTitle] = useState("");
    const [reasonError, setReasonError] = useState("");
    const [creatingReason, setCreatingReason] = useState(false);

    const currentDateKey = task?.dueDate ? formatDate(task.dueDate, "YYYY-MM-DD") : null;
    const currentDueMs = task?.dueDate ? new Date(task.dueDate).getTime() : null;
    const newDateKey = newDueAt ? formatDate(newDueAt, "YYYY-MM-DD") : null;
    const newDateValid = Boolean(newDueAt) && (currentDueMs === null || newDueAt.getTime() > currentDueMs);
    const isRecurringTask = Boolean(task?.series);

    const chooseQuick = (key) => {
        const base = currentDueMs ?? Date.now();
        const minutes = {"30m": 30, "1h": 60, "2h": 120, tomorrow: 24 * 60}[key];
        setNewDueAt(new Date(base + minutes * 60 * 1000));
        setQuickChoice(key);
        setError("");
    };

    const resetForm = useCallback(() => {
        setSelectedReasonId(null);
        setNewDueAt(null);
        setQuickChoice(null);
        setNewAssignee(null);
        setNote("");
        setNotifyRelatedUsers(true);
        setError("");
        setSubmitting(false);
        setAddingReason(false);
        setNewReasonTitle("");
        setReasonError("");
    }, []);

    useEffect(() => {
        if (visible) {
            resetForm();
            setLoading(true);
            Promise.all([
                taskDelayReason.getDelayReasons(),
                taskDelay.getTaskDelays(task?.id ?? task?.documentId),
            ]).then(([reasonsList, delaysList]) => {
                setReasons(reasonsList || []);
                setDelays(delaysList || []);
            }).catch(() => {
                setError(translate("task-delay.load-error"));
            }).finally(() => {
                setLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, task?.id, task?.documentId]);

    const closeModal = () => {
        if (submitting) return;
        if (typeof setVisible === "function") setVisible(false);
    };

    const handleAddReason = async () => {
        const title = (newReasonTitle || "").trim();
        if (!title) {
            setReasonError(translate("task-delay.reason-empty-error"));
            return;
        }
        if (title.length > REASON_MAX_LENGTH) {
            setReasonError(translate("task-delay.reason-length-error"));
            return;
        }

        const duplicate = reasons.find((reason) => normalizeTitle(reason?.title) === normalizeTitle(title));
        if (duplicate) {
            setSelectedReasonId(duplicate?.id ?? duplicate?.documentId);
            setAddingReason(false);
            setNewReasonTitle("");
            setReasonError("");
            setError("");
            return;
        }

        setCreatingReason(true);
        setReasonError("");
        try {
            const created = await taskDelayReason.createDelayReason({title});
            if (created) {
                const key = created?.id ?? created?.documentId;
                setReasons((current) => {
                    const next = [created, ...current];
                    const seen = new Set();
                    return next.filter((reason) => {
                        const itemKey = reason?.id ?? reason?.documentId;
                        if (itemKey == null || seen.has(itemKey)) return false;
                        seen.add(itemKey);
                        return true;
                    });
                });
                setSelectedReasonId(key);
                setAddingReason(false);
                setNewReasonTitle("");
            }
        } catch (e) {
            setReasonError(localizedTaskDelayError(e, translate, "task-delay.reason-create-error"));
        } finally {
            setCreatingReason(false);
        }
    };

    const handleSubmit = async () => {
        if (submitting || !(task?.id || task?.documentId)) return;

        if (!newDueAt) {
            setError(translate("task-delay.date-required-error"));
            return;
        }
        if (!newDateValid) {
            setError(translate("task-defer.time-after-error"));
            return;
        }

        setSubmitting(true);
        setError("");
        try {
            const newAssigneeId = newAssignee?.id ?? newAssignee?.documentId ?? null;
            const payload = {
                newDueAt: newDueAt.toISOString(),
                note: note.trim() || null,
                notifyRelatedUsers,
            };
            if (selectedReasonId) payload.delayReasonId = selectedReasonId;
            if (newAssigneeId) payload.newAssigneeId = newAssigneeId;

            const {task: updated, warnings} = await taskDelay.createTaskDelay(task?.id ?? task?.documentId, payload);

            // The server already moved due/start (same shift) and recorded the exception; re-sending the same values
            // only refreshes the local task state and creates no extra history entry.
            if (updateTask && updated) {
                const syncData = {dueDate: new Date(updated.dueDate)};
                if (updated.startAt) syncData.startAt = new Date(updated.startAt);
                if (newAssigneeId) syncData.assignee = newAssignee;
                await updateTask(task, syncData);
            }

            dispatch(setMessage({severity: "success", content: translate("task-delay.success")}));
            (warnings || []).forEach((warning) => {
                dispatch(setMessage({severity: "warn", content: translate(warning)}));
            });
            resetForm();
            setVisible(false);
        } catch (e) {
            setError(localizedTaskDelayError(e, translate, "task-delay.submit-error"));
            setSubmitting(false);
        }
    };

    const canSubmit = newDateValid && !submitting;
    const projectTitle = task?.projects?.length ? task.projects[0]?.title : null;
    const reasonOptions = reasons.map((reason) => ({
        label: reason?.title ?? translate("task-delay.unknown-reason"),
        value: reason?.id ?? reason?.documentId,
    }));

    return (
        <Dialog
            showCloseIcon={false}
            headerClassName="border-round-top-2xl border-bottom-1 border-200 p-3"
            pt={{
                root: () => ({className: "rtl border-round-2xl"}),
                header: () => ({className: "flex-shrink-0"}),
                content: () => ({className: "p-0 flex flex-column overflow-hidden flex-1 min-h-0"}),
                footer: () => ({className: "p-0 flex-shrink-0 border-round-bottom-2xl"}),
            }}
            style={{maxHeight: "85vh", display: "flex", flexDirection: "column"}}
            className="w-11 sm:w-9 md:w-7 lg:w-5 border-round-2xl overflow-hidden"
            header={
                <div className="flex align-items-center justify-content-between gap-3 w-full p-3 pb-2">
                    <div className="flex align-items-center gap-3">
                        <span className="flex align-items-center justify-content-center w-3rem h-3rem border-circle bg-primary-50 text-primary flex-shrink-0">
                            <Icon icon="mi mi:history" className="text-xl"/>
                        </span>
                        <div className="flex flex-column">
                            <span className="text-sm font-bold text-900">{translate(isRecurringTask ? "task-defer.title" : "task-defer.title-plain")}</span>
                            <span className="text-2xs text-500">{translate("task-defer.subtitle")}</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={closeModal}
                        className="flex-shrink-0 w-2rem h-2rem border-circle border-none bg-transparent flex align-items-center justify-content-center cursor-pointer hover:surface-100 transition-duration-150"
                        aria-label={translate("close")}
                    >
                        <Icon icon="mi mi:close" className="text-lg text-500"/>
                    </button>
                </div>
            }
            footer={
                <div className="flex flex-row-reverse align-items-center gap-2 justify-content-between p-3 surface-50 border-top-1 border-200 flex-shrink-0">
                    <div className="flex align-items-center gap-2">
                        <Button
                            disabled={submitting}
                            className="text-xs p-button-secondary p-button-outlined p-2 pl-3 pr-3 border-round-lg"
                            label={translate("cancel")}
                            onClick={closeModal}
                        />
                        <Button
                            className="text-xs gap-2 p-2 pl-3 pr-3 border-round-lg"
                            label={translate(submitting ? "task-delay.submitting" : "task-delay.submit")}
                            icon={<Icon icon="mi mi:send" className="text-lg"/>}
                            loading={submitting}
                            disabled={!canSubmit}
                            onClick={handleSubmit}
                        />
                    </div>
                </div>
            }
            visible={visible}
            onHide={closeModal}
        >
            <div className="flex flex-column h-full min-h-0">
                <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-column gap-3">
                    {error && <Message className="text-xs w-full" severity="error" text={error}/>} 
                    {loading ? (
                        <div className="flex flex-column align-items-center justify-content-center gap-2 p-8 text-xs text-500">
                            <ProgressSpinner className="w-2rem h-2rem" strokeWidth="4"/>
                            <span>{translate("task-delay.loading")}</span>
                        </div>
                    ) : (
                        <>
                            <div className="flex align-items-center justify-content-between gap-3 p-3 surface-50 border-1 border-200 border-round-xl">
                                <div className="flex flex-column gap-1">
                                    <span className="text-2xs text-500">{translate("task-delay.current-due-date")}</span>
                                    {currentDateKey ? (
                                        <Span spanClassName="text-sm font-bold" type="PERSIAN_DATE" format={"dddd\u060C DD MMMM YYYY HH:mm"}>{task?.dueDate}</Span>
                                    ) : (
                                        <span className="text-sm font-bold text-500">---</span>
                                    )}
                                </div>
                                <Icon icon="mi mi:arrow_forward" className="text-xl text-primary"/>
                                <div className="flex flex-column gap-1">
                                    <span className="text-2xs text-500">{translate("task-delay.new-due-date-label")}</span>
                                    {newDateKey ? (
                                        <Span spanClassName="text-sm font-bold text-primary" type="PERSIAN_DATE" format={"dddd\u060C DD MMMM YYYY HH:mm"}>{newDueAt}</Span>
                                    ) : (
                                        <span className="text-sm font-bold text-500">{translate("task-delay.not-selected")}</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-column gap-2">
                                <InputLabel label={translate("task-defer.reason-optional")}>
                                    <Dropdown
                                        className="w-full text-xs"
                                        panelClassName="rtl"
                                        value={selectedReasonId}
                                        showClear
                                        options={reasonOptions}
                                        placeholder={translate("task-delay.reason-placeholder")}
                                        emptyMessage={translate("task-delay.no-reasons")}
                                        onChange={(e) => {
                                            setSelectedReasonId(e.value);
                                            setError("");
                                        }}
                                        pt={{input: () => ({className: "text-xs"}), item: () => ({className: "text-xs"})}}
                                    />
                                </InputLabel>

                                {addingReason ? (
                                    <div className="flex flex-column gap-2 p-2 surface-50 border-1 border-200 border-round-lg">
                                        <InputText
                                            autoFocus
                                            value={newReasonTitle}
                                            onChange={(e) => {
                                                setNewReasonTitle(e.target.value);
                                                setReasonError("");
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleAddReason();
                                                }
                                            }}
                                            maxLength={REASON_MAX_LENGTH}
                                            className="w-full text-xs"
                                            placeholder={translate("task-delay.new-reason-placeholder")}
                                        />
                                        <div className="flex align-items-center gap-2">
                                            <Button
                                                className="text-2xs p-button-sm p-button-secondary p-2 pl-3 pr-3 border-round-lg"
                                                label={translate("task-delay.add-reason-save")}
                                                loading={creatingReason}
                                                disabled={creatingReason}
                                                onClick={handleAddReason}
                                            />
                                            <Button
                                                className="text-2xs p-button-text p-1 pl-2 pr-2"
                                                label={translate("cancel")}
                                                disabled={creatingReason}
                                                onClick={() => {
                                                    setAddingReason(false);
                                                    setNewReasonTitle("");
                                                    setReasonError("");
                                                }}
                                            />
                                        </div>
                                        {reasonError && <span className="text-2xs text-red-500">{reasonError}</span>}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setAddingReason(true)}
                                        className="p-0 border-none bg-transparent cursor-pointer flex align-items-center gap-1 text-2xs font-medium text-primary hover:text-primary-700"
                                    >
                                        <Icon icon="mi mi:add" className="text-sm"/>
                                        <span>{translate("task-delay.add-reason")}</span>
                                    </button>
                                )}
                            </div>

                            {isRecurringTask && (
                                <div className="flex align-items-start gap-2 p-3 bg-blue-50 border-1 border-blue-200 border-round-lg text-2xs line-height-3 text-blue-700">
                                    <Icon icon="mi mi:repeat" className="text-lg flex-shrink-0"/>
                                    <span>
                                        {translate("task-defer.recurring-hint")}
                                        {task?.recurrence?.nextScheduledDueAt && (
                                            <> {translate("task-defer.next-occurrence")}: <Span spanClassName="font-bold" type="PERSIAN_DATE" format={"dddd\u060C DD MMMM HH:mm"}>{task.recurrence.nextScheduledDueAt}</Span></>
                                        )}
                                    </span>
                                </div>
                            )}

                            <div className="grid">
                                <InputLabel require className="col-12 md:col-6" label={translate("task-defer.new-due-label")}>
                                    <div className="flex flex-wrap gap-1 mb-2">
                                        {["30m", "1h", "2h", "tomorrow"].map((key) => (
                                            <Button
                                                key={key}
                                                type="button"
                                                label={translate(`task-defer.quick.${key}`)}
                                                outlined={quickChoice !== key}
                                                className="text-2xs p-1 px-2 border-round-lg"
                                                onClick={() => chooseQuick(key)}
                                            />
                                        ))}
                                    </div>
                                    <div className="flex align-items-center border-1 border-200 border-round-lg p-1 px-2">
                                        <SelectZofinoDateTime
                                            fullWidth
                                            selectedDateTime={{start: null, end: newDueAt ? Math.floor(newDueAt.getTime() / 1000) : null}}
                                            onChange={(e) => {
                                                if (e.end) setNewDueAt(new Date(e.end.toUnix() * 1000));
                                                setQuickChoice("custom");
                                                setError("");
                                            }}
                                        />
                                    </div>
                                    {newDateKey && !newDateValid && (
                                        <span className="text-2xs text-red-500">{translate("task-defer.time-after-error")}</span>
                                    )}
                                </InputLabel>
                                <InputLabel className="col-12 md:col-6" label={translate("task-delay.new-assignee-label")}>
                                    <div className="flex align-items-center border-1 border-200 border-round-lg p-1 px-2">
                                        <SelectMember
                                            label={translate("task-delay.no-assignee-change")}
                                            selectedMember={newAssignee}
                                            members={members}
                                            fullWidth
                                            onChange={setNewAssignee}
                                        />
                                    </div>
                                </InputLabel>
                            </div>

                            <InputLabel label={`${translate("task-delay.note-label")} (${translate("task-delay.optional")})`}>
                                <InputTextarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                                    rows={2}
                                    className="w-full text-xs"
                                    placeholder={translate("task-delay.note-placeholder")}
                                />
                            </InputLabel>

                            {projectTitle && (
                                <div className="flex align-items-start gap-2 p-3 bg-orange-50 border-1 border-orange-200 border-round-lg text-2xs line-height-3 text-orange-700">
                                    <Icon icon="mi mi:warning" className="text-lg flex-shrink-0"/>
                                    <span>{translate("task-delay.impact-hint")} {projectTitle && <b>{projectTitle}</b>} {translate("task-delay.impact-hint-suffix")}</span>
                                </div>
                            )}

                            <div className="flex align-items-center justify-content-between gap-3 border-1 border-200 border-round-lg p-3">
                                <div className="flex flex-column gap-1">
                                    <span className="text-xs font-medium">{translate("task-delay.notify-label")}</span>
                                    <span className="text-2xs text-500">{translate("task-delay.notify-description")}</span>
                                </div>
                                <InputSwitch checked={notifyRelatedUsers} onChange={(e) => setNotifyRelatedUsers(e.value)}/>
                            </div>

                            <div className="border-top-1 border-200 pt-3 flex flex-column gap-2">
                                <span className="text-xs font-medium text-700">{translate("task-delay.history-title")}</span>
                                {delays.length === 0 ? (
                                    <div className="flex flex-column align-items-center gap-2 p-4 text-xs text-400">
                                        <Icon icon="mi mi:history_toggle_off" className="text-2xl text-300"/>
                                        <span>{translate("task-delay.no-history")}</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-column gap-2">
                                        {delays.map((delay) => {
                                            const key = delay?.documentId ?? delay?.id;
                                            const createdBy = delay?.createdBy?.member;
                                            const assigneeChanged = delay?.previousAssigneeId && delay?.newAssigneeId && delay?.previousAssigneeId !== delay?.newAssigneeId;
                                            return (
                                                <div key={key} className="flex gap-2 p-2 border-1 border-200 border-round-xl">
                                                    <Avatar memberData={createdBy} size="small" className="text-3xs flex-shrink-0" style={{width: "1.5rem", height: "1.5rem"}}/>
                                                    <div className="flex-1 min-w-0 flex flex-column gap-1">
                                                        <div className="flex align-items-center gap-2 text-xs">
                                                            {(delay?.reasonSnapshot || delay?.reason?.title) && <span className="font-medium">{delay?.reasonSnapshot ?? delay?.reason?.title}</span>}
                                                            {delay?.createdAt && (
                                                                <Span spanClassName="text-2xs text-400" type="PERSIAN_DATE" format="YYYY/MM/DD HH:mm">{delay.createdAt}</Span>
                                                            )}
                                                        </div>
                                                        {(delay?.previousDueAt || delay?.previousDueDate) && (delay?.newDueAt || delay?.newDueDate) && (
                                                            <div className="flex align-items-center gap-1 text-2xs text-500">
                                                                <Span spanClassName="line-through" type="PERSIAN_DATE" format={delay.previousDueAt ? "YYYY/MM/DD HH:mm" : "YYYY/MM/DD"}>{delay.previousDueAt || delay.previousDueDate}</Span>
                                                                <Icon icon="mi mi:arrow_forward" className="text-xs"/>
                                                                <Span spanClassName="text-primary" type="PERSIAN_DATE" format={delay.newDueAt ? "YYYY/MM/DD HH:mm" : "YYYY/MM/DD"}>{delay.newDueAt || delay.newDueDate}</Span>
                                                                {delay?.wasOverdueAtAction && <span className="text-orange-600">· {translate("task-defer.after-due")}</span>}
                                                            </div>
                                                        )}
                                                        {assigneeChanged && (
                                                            <span className="text-2xs text-500">
                                                                <Icon icon="mi mi:swap_horiz" className="text-xs"/>
                                                                <span className="ml-1">{translate("task-delay.assignee-changed")}</span>
                                                            </span>
                                                        )}
                                                        {delay?.note && (
                                                            <span className="text-2xs text-500">{delay.note}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Dialog>
    );
}

export default TaskDelayModal;
